import config from '../config';
import moment from 'moment';
import axios from 'axios';

const CLASS_LIST_ROUTE = '/student/class';

const groupInstance = axios.create({
  baseURL: config.api,
  headers: {
    'Content-Type': 'application/json'
  }
});

const exportToCSV = (csvString, filename) => {
  const blob = new Blob(['\uFEFF' + csvString], { type: 'text/csv;charset=utf-8;' });
  let href;
  if (window.navigator.msSaveOrOpenBlob) {
    href = window.navigator.msSaveBlob(blob, filename);
  } else {
    href = window.URL.createObjectURL(blob);
  }
  const downloadLink = window.document.createElement('a');
  downloadLink.href = href;
  downloadLink.download = filename;
  downloadLink.setAttribute('download', filename);
  window.document.body.appendChild(downloadLink);
  downloadLink.click();
  window.document.body.removeChild(downloadLink);
  if (typeof href === 'string') {
    window.URL.revokeObjectURL(href);
  }
};

export const classGenCSV = async (id = '', options) => {
  const classGetResult = async (classQuotaId = '') => {
    let allData = [];
    let page = 1;
    const perPage = 5000;
    let total = null;

    try {
      while (true) {
        const params = { page, perPage };
        if (classQuotaId) {
          params.classQuotaId = classQuotaId;
        }
        const response = await groupInstance.get(CLASS_LIST_ROUTE, { params });
        const pageResults = response.data.results || [];
        total = typeof response.data.total === 'number' ? response.data.total : total;

        allData = allData.concat(pageResults);

        if (total !== null) {
          if (page * perPage >= total) {
            break;
          }
        } else if (pageResults.length < perPage) {
          break;
        }

        page++;
      }
      return allData;
    } catch (error) {
      if (error.response && error.response.status === 400 && allData.length > 0) {
        console.warn('No more data available, returning collected data.');
        return allData;
      } else {
        console.error('Error fetching data:', error);
        throw error;
      }
    }
  };

  const getOption = (oid) => {
    let target = options.filter(o => o._id === oid)[0];
    return target ? target.option_tc : '';
  }

  const getDiff = (data) => {
    let allValues = Object.keys(data).map(key => key !== '_id' && data[key]).filter(e => e !== false);
    return allValues.reduce((a, b) => Math.max(a, b)) - allValues.reduce((a, b) => Math.min(a, b));
  }

  const getRanking = (arr) => {
    if (arr && arr.length > 0) {
      return arr.join(',');
    }
    return '';
  }

  let header = [];
  header.push('學校');
  header.push('年度');
  header.push('班別');
  header.push('問卷編碼');
  header.push('學生姓名[中文]');
  header.push('學生姓名[英文]');
  header.push('性別');
  header.push('年齡');
  header.push('編號');
  header.push('DSE選修[1]');
  header.push('DSE選修[2]');
  header.push('DSE選修[3]');
  header.push('父親教育程度');
  header.push('母親教育程度');
  header.push('夢想職業[1]');
  header.push('夢想職業[2]');
  header.push('夢想職業[3]');
  header.push('夢想職業[4]');
  header.push('夢想職業[5]');
  header.push('夢想職業[6]');
  header.push('活動[r]');
  header.push('活動[i]');
  header.push('活動[a]');
  header.push('活動[s]');
  header.push('活動[e]');
  header.push('活動[c]');
  header.push('技能[r]');
  header.push('技能[i]');
  header.push('技能[a]');
  header.push('技能[s]');
  header.push('技能[e]');
  header.push('技能[c]');
  header.push('職業[r]');
  header.push('職業[i]');
  header.push('職業[a]');
  header.push('職業[s]');
  header.push('職業[e]');
  header.push('職業[c]');
  header.push('能力1[r]');
  header.push('能力1[i]');
  header.push('能力1[a]');
  header.push('能力1[s]');
  header.push('能力1[e]');
  header.push('能力1[c]');
  header.push('能力2[r]');
  header.push('能力2[i]');
  header.push('能力2[a]');
  header.push('能力2[s]');
  header.push('能力2[e]');
  header.push('能力2[c]');
  header.push('總和[r]');
  header.push('總和[i]');
  header.push('總和[a]');
  header.push('總和[s]');
  header.push('總和[e]');
  header.push('總和[c]');
  header.push('分差[活動]');
  header.push('分差[技能]');
  header.push('分差[職業]');
  header.push('分差[能力1]');
  header.push('分差[能力2]');
  header.push('分差[總分]');
  header.push('可靠度[活動]');
  header.push('可靠度[技能]');
  header.push('可靠度[職業]');
  header.push('可靠度[能力1]');
  header.push('可靠度[能力2]');
  header.push('可靠度[總分]');
  header.push('排位[1]');
  header.push('排位[2]');
  header.push('排位[3]');
  header.push('排位[4]');
  header.push('排位[5]');
  header.push('排位[6]');

  try {
    const res = await classGetResult(id);
    let result = res.results || res; // Assuming 'results' might be the top-level key or the whole response is the result
    let csvString = result.filter(r => r.progress === 'done').map((student, i) => {
      let row = [];
      if (student.progress !== 'done' || student.info == undefined) {
        for (let j = 0; j < 74; j++) {
          row.push('');
        }
      } else {
        row.push(`"${student.school.name_tc || student.school.name_en}"`);
        row.push(`[${student.schoolQuota.year}]`);
        row.push(`"${student.classData.className}"`);
        row.push(`"${student.studentCode}"`);
        row.push(`"${student.info.nameChinese}"`);
        row.push(`"${student.info.nameEnglish}"`);
        row.push(`"${student.info.gender || ''}"`);
        row.push(`"${student.info.age}"`);
        row.push(`"${student.info.classNumber}"`);
        row.push(`"${getOption(student.info.elective1)}"`);
        row.push(`"${getOption(student.info.elective2)}"`);
        row.push(`"${getOption(student.info.elective3)}"`);
        row.push(`"${getOption(student.info.educationFather)}"`);
        row.push(`"${getOption(student.info.educationMother)}"`);
        row.push(`"${(student.info.occupations || [])[0] || ''}"`);
        row.push(`"${(student.info.occupations || [])[1] || ''}"`);
        row.push(`"${(student.info.occupations || [])[2] || ''}"`);
        row.push(`"${(student.info.occupations || [])[3] || ''}"`);
        row.push(`"${(student.info.occupations || [])[4] || ''}"`);
        row.push(`"${(student.info.occupations || [])[5] || ''}"`);
        row.push(student.activitiesScore.R);
        row.push(student.activitiesScore.I);
        row.push(student.activitiesScore.A);
        row.push(student.activitiesScore.S);
        row.push(student.activitiesScore.E);
        row.push(student.activitiesScore.C);
        row.push(student.competenciesScore.R);
        row.push(student.competenciesScore.I);
        row.push(student.competenciesScore.A);
        row.push(student.competenciesScore.S);
        row.push(student.competenciesScore.E);
        row.push(student.competenciesScore.C);
        row.push(student.occupationsScore.R);
        row.push(student.occupationsScore.I);
        row.push(student.occupationsScore.A);
        row.push(student.occupationsScore.S);
        row.push(student.occupationsScore.E);
        row.push(student.occupationsScore.C);
        row.push(student.self1Score.R);
        row.push(student.self1Score.I);
        row.push(student.self1Score.A);
        row.push(student.self1Score.S);
        row.push(student.self1Score.E);
        row.push(student.self1Score.C);
        row.push(student.self2Score.R);
        row.push(student.self2Score.I);
        row.push(student.self2Score.A);
        row.push(student.self2Score.S);
        row.push(student.self2Score.E);
        row.push(student.self2Score.C);
        row.push(student.totalScore.R);
        row.push(student.totalScore.I);
        row.push(student.totalScore.A);
        row.push(student.totalScore.S);
        row.push(student.totalScore.E);
        row.push(student.totalScore.C);
        row.push(getDiff(student.activitiesScore));
        row.push(getDiff(student.competenciesScore));
        row.push(getDiff(student.occupationsScore));
        row.push(getDiff(student.self1Score));
        row.push(getDiff(student.self2Score));
        row.push(getDiff(student.totalScore));
        row.push(getDiff(student.activitiesScore) <= 3 ? 'X' : '');
        row.push(getDiff(student.competenciesScore) <= 3 ? 'X' : '');
        row.push(getDiff(student.occupationsScore) <= 3 ? 'X' : '');
        row.push(getDiff(student.self1Score) <= 3 ? 'X' : '');
        row.push(getDiff(student.self2Score) <= 3 ? 'X' : '');
        row.push(getDiff(student.totalScore) <= 3 ? 'X' : '');
        row.push(`"${getRanking(student.ranking[0])}"`);
        row.push(`"${getRanking(student.ranking[1])}"`);
        row.push(`"${getRanking(student.ranking[2])}"`);
        row.push(`"${getRanking(student.ranking[3])}"`);
        row.push(`"${getRanking(student.ranking[4])}"`);
        row.push(`"${getRanking(student.ranking[5])}"`);
      }
      return row.join(',');
    }).join("\n");

    const completedResults = result.filter(r => r.progress === 'done');
    if (completedResults.length === 0) {
      return { count: 0 };
    }

    const filename = id
      ? `${completedResults[0].classData.groupId}_summary_report.csv`
      : `all_classes_summary_report_${moment().format('YYYYMMDD_HHmmss')}.csv`;

    exportToCSV(header.join(',') + '\n' + csvString, filename);
    return { count: completedResults.length, filename };
  } catch (error) {
    console.error('Error in classGenCSV:', error);
    throw error; // Re-throw for upper-level error handling if necessary
  }
};
